import { Box } from '@mui/material';
import { useInfiniteQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { memo, useCallback, useMemo } from 'react';
import { useDataProvider } from 'react-admin';
import { useTranslation } from 'react-i18next';
import { useCosmos } from 'providers/contexts/CosmosContext';
import { CoreLogSearchResult } from 'providers/contexts/extensions/CoreLogs';
import { ApiErrorResponse, BloxCustomDataProviderType } from 'utility/types/dataProvider';
import { LogViewer, LogViewerRow } from './LogViewer';
import { LogSearchTopBar } from './LogSearchTopBar';
import { useLogSearch } from './LogSearchProvider';
import { LOGS_PER_PAGE } from './logSearchConstants';
import ErrorAlerts from '../ErrorAlerts';
import ReplayIcon from '@mui/icons-material/Replay';

// UTC day handling
dayjs.extend(utc);

const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Resolve a date-range value to an API ISO bound. A date-only `YYYY-MM-DD`
 * (e.g. an initial range with no time) is expanded to the UTC start/end of day;
 * a full date-time (the user picked a time) is passed through as-is.
 */
const toIsoBound = (value?: string, edge?: 'start' | 'end'): string | undefined => {
  if (!value) {
    return undefined;
  }
  const parsed = dayjs.utc(value);
  if (!parsed.isValid()) {
    return undefined;
  }
  if (DATE_ONLY.test(value)) {
    return edge === 'start'
      ? parsed.startOf('day').toISOString()
      : parsed.endOf('day').toISOString();
  }
  return parsed.toISOString();
};

/**
 * Log Search Modal content: runs the token-paginated log query off the
 * debounced filters and renders the search bar + results grid. Clicking a row
 * opens the read-only details modal (owned by `LogViewer`).
 */
export const LogSearchContent = memo(() => {
  const { t } = useTranslation();
  const strRetry = t('blox.actions.retry');
  const dataProvider = useDataProvider<BloxCustomDataProviderType>();
  const { sessionConfig } = useCosmos();
  const {
    log,
    fixedFields,
    toRow,
    transformRows,
    enabled,
    showDriver,
    labels,
    dbFilters,
  } = useLogSearch();
  const message = dbFilters.query || undefined;
  const level = dbFilters.level || undefined;
  const startDate = toIsoBound(dbFilters.start_date, 'start');
  const endDate = toIsoBound(dbFilters.end_date, 'end');

  const {
    data,
    isLoading,
    isFetching,
    error,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
    refetch,
  } = useInfiniteQuery<CoreLogSearchResult, ApiErrorResponse>({
    queryKey: [
      'core/logs/system/search',
      'getCoreLogsSystemSearch',
      log,
      fixedFields,
      message,
      level,
      startDate,
      endDate,
    ],
    queryFn: async ({ pageParam }: any) => {
      const result = await dataProvider.getCoreLogsSystemSearch({
        log: log,
        fields: fixedFields,
        message: message,
        level: level && level.length ? level : undefined,
        start_date: startDate,
        end_date: endDate,
        page: pageParam,
        limit: LOGS_PER_PAGE,
      });
      return result;
    },
    initialPageParam: '',
    getNextPageParam: (lastPage) => {
      // The log backend reports no total: roll forward until a page is empty.
      const items = lastPage?.data?.items ?? [];
      if (items.length === 0) {
        return undefined;
      }
      return lastPage?.data?.next ?? undefined;
    },
    enabled: enabled,
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    gcTime: 0, // keep until modal close
    staleTime: 0, // always refresh when modal open
  });

  const rows: LogViewerRow[] = useMemo(() => {
    if (!data) {
      return [];
    }

    const mapped = data.pages.flatMap((page) => page?.data?.items ?? []).map(toRow);

    if (transformRows) {
      return transformRows(mapped);
    }
    return mapped;
  }, [data, toRow, transformRows]);

  const handleRefetch = useCallback(() => {
    refetch();
  }, [refetch]);

  if (error) {
    return (
      <ErrorAlerts
        severity="warning"
        buttonText={strRetry}
        buttonIcon={<ReplayIcon />}
        buttonOnClick={handleRefetch}
        showDetails={true}
        errors={[
          {
            status: `${error?.status || error?.axiosStatus}`,
            message: `${error?.message || error?.axiosMessage}`,
            fullDetails: error,
          },
        ]}
      />
    );
  }

  return (
    <Box
      data-testid="log-search-content"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        width: '100%',
      }}
    >
      <LogSearchTopBar isFetching={isFetching} />
      <LogViewer
        dataTestId="log-search-list"
        rows={rows}
        isLoading={isLoading}
        hasNextPage={hasNextPage}
        isFetchingNextPage={isFetchingNextPage}
        fetchNextPage={fetchNextPage}
        siteTimeZone={sessionConfig?.time_zone}
        showDriver={showDriver}
        labels={labels}
      />
    </Box>
  );
});

LogSearchContent.displayName = 'LogSearchContent';
